import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { InstallAppButton } from "../components/pwa/InstallAppButton";
import { useAuth } from "../context/AuthContext";
import { useBranding, resolveLogo } from "../hooks/useBranding";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Mail, Lock, User, Building2, Phone, MapPin, FileText, ArrowRight,
  ArrowLeft, Upload, X, CheckCircle2, ShieldCheck, Fingerprint,
} from "lucide-react";

const inputCls = "h-11 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus-visible:ring-cyan-400/40";
const labelCls = "text-zinc-300";

const LoginPage = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(urlParams.get("plan") ? "register" : "login");
  const [planChoice, setPlanChoice] = useState(urlParams.get("plan") === "bronze" ? "bronze" : "ouro");
  const [cycleChoice, setCycleChoice] = useState(urlParams.get("cycle") === "annual" ? "annual" : "monthly");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = dados pessoais, 2 = dados empresa
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);

  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });
  const [twoFA, setTwoFA] = useState({ show: false, tempToken: "", code: "" });

  const [registerForm, setRegisterForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    company_name: "",
    company_nif: "",
    company_address: "",
    company_phone: "",
    company_email: "",
    company_logo: null,
  });

  const { login, verify2FA, register, loginWithGoogle, isAuthenticated, deviceUnlocked, loading: authLoading } = useAuth();
  const { branding } = useBranding();
  const branded = branding.branded;
  const brandLogo = branded ? resolveLogo(branding.logo) : "";
  const brandName = branded ? branding.company_name || "A sua empresa" : "LUMORA";
  const accent = branded && branding.brand_color ? branding.brand_color : null;
  const accentStyle = accent ? { backgroundColor: accent, color: "#fff" } : undefined;
  const navigate = useNavigate();
  const redirectRef = useRef(null);

  const from = location.state?.from?.pathname || "/dashboard";
  const classic = new URLSearchParams(location.search).get("classic") === "1";

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectRef.current || from, { replace: true });
      return;
    }
    // Dispositivo já desbloqueado → mostrar perfis (salvo pedido explícito de login clássico)
    if (!authLoading && deviceUnlocked && !classic) navigate("/perfis", { replace: true, state: location.state });
  }, [isAuthenticated, navigate, from, deviceUnlocked, classic, authLoading, location.state]);

  const afterPasswordLogin = (data) => {
    toast.success("Login efetuado com sucesso!");
    // Admin/gerente desbloqueou o dispositivo → vai directamente para "Escolher utilizador"
    const target = data?.device_unlocked ? "/perfis" : from;
    redirectRef.current = target;
    navigate(target, { replace: true, state: { from: location.state?.from } });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await login(loginForm.email, loginForm.password);
      if (data.requires_2fa) {
        setTwoFA({ show: true, tempToken: data.temp_token, code: "" });
        toast.info("Introduza o código da sua app autenticadora");
        return;
      }
      afterPasswordLogin(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Email ou palavra-passe incorretos");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await verify2FA(twoFA.tempToken, twoFA.code);
      afterPasswordLogin(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Código 2FA inválido");
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterStep1 = (e) => {
    e.preventDefault();

    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error("As palavras-passe não coincidem");
      return;
    }

    if (registerForm.password.length < 6) {
      toast.error("A palavra-passe deve ter pelo menos 6 caracteres");
      return;
    }

    setStep(2);
  };

  const handleRegisterStep2 = async (e) => {
    e.preventDefault();

    if (!registerForm.company_name.trim()) {
      toast.error("O nome da empresa é obrigatório");
      return;
    }

    if (!registerForm.company_nif.trim()) {
      toast.error("O NIF da empresa é obrigatório");
      return;
    }

    setLoading(true);

    try {
      await register({
        email: registerForm.email,
        password: registerForm.password,
        name: registerForm.name,
        company_name: registerForm.company_name,
        company_nif: registerForm.company_nif,
        company_address: registerForm.company_address,
        company_phone: registerForm.company_phone,
        company_email: registerForm.company_email,
        company_logo: registerForm.company_logo,
        plan: planChoice,
        billing_cycle: cycleChoice,
      });
      toast.success("Conta e empresa criadas com sucesso! Tem 14 dias de teste grátis. Faça login.");
      setActiveTab("login");
      setLoginForm({ ...loginForm, email: registerForm.email });
      setStep(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Por favor, selecione uma imagem");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      setRegisterForm({ ...registerForm, company_logo: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setRegisterForm({ ...registerForm, company_logo: null });
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0A0A0A] text-white" style={{ fontFamily: "'Satoshi', Inter, sans-serif" }} data-testid="login-page">
      <style>{`
        .lumora-heading { font-family: 'Cabinet Grotesk', 'Outfit', Inter, sans-serif; }
        @keyframes lumora-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
      `}</style>
      <div className="pointer-events-none absolute -top-40 -right-32 h-[500px] w-[500px] rounded-full bg-cyan-500/10 blur-[130px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-32 h-[420px] w-[420px] rounded-full bg-blue-600/10 blur-[130px]" />

      {/* Header */}
      <header className="relative z-10 flex h-16 items-center justify-between border-b border-white/10 bg-black/40 px-6 backdrop-blur-xl">
        <Link to="/" className="lumora-heading flex items-center gap-2.5 text-lg font-extrabold tracking-tight" data-testid="login-logo-link">
          {brandLogo ? <img src={brandLogo} alt={brandName} className="h-8 w-8 rounded-md object-contain bg-white/5" data-testid="login-brand-logo" /> : <img src="/lumora-logo.png" alt="Lumora" className="h-8 w-8" />}
          <span data-testid="login-brand-name">{brandName}</span>
        </Link>
        <div className="flex items-center gap-2">
          <InstallAppButton variant="ghost" className="hidden sm:inline-flex rounded-full border border-white/15 text-zinc-300 hover:border-white/40 hover:text-white hover:bg-transparent" />
          {!branded && (
            <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-zinc-300 transition-colors duration-200 hover:border-white/40 hover:text-white" data-testid="back-to-landing-btn">
              <ArrowLeft className="h-4 w-4" /> Voltar à página inicial
            </Link>
          )}
        </div>
      </header>

      <div className="relative z-10 mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-16 px-6 py-12 lg:grid-cols-2">
        {/* Branding */}
        <div className="hidden lg:block" data-testid="login-branding-panel">
          {brandLogo ? (
            <img src={brandLogo} alt={brandName} className="h-24 w-24 rounded-2xl object-contain bg-white/5 p-2" style={{ animation: "lumora-float 6s ease-in-out infinite" }} />
          ) : (
            <img src="/lumora-logo.png" alt="Lumora" className="h-20 w-20" style={{ animation: "lumora-float 6s ease-in-out infinite" }} />
          )}
          {branded ? (
            <>
              <h1 className="lumora-heading mt-8 text-4xl font-black leading-tight tracking-tighter xl:text-5xl" style={accent ? { color: accent } : undefined}>
                {brandName}
              </h1>
              <p className="mt-4 max-w-md text-zinc-400">{branding.tagline || "Área reservada à equipa. Inicie sessão com a sua conta para continuar."}</p>
              <p className="mt-10 text-xs uppercase tracking-[0.2em] text-zinc-600 inline-flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-cyan-500/70" /> Powered by LUMORA
              </p>
            </>
          ) : (
            <>
              <h1 className="lumora-heading mt-8 text-4xl font-black leading-tight tracking-tighter xl:text-5xl">
                Toda a gestão da sua<br />empresa, num só lugar.
              </h1>
              <p className="mt-4 max-w-md text-zinc-400">
                POS, faturação AGT, stock, finanças, RH e muito mais — 100% em Kwanza e em Português.
              </p>
              <div className="mt-8 space-y-3">
                {[
                  "Faturação certificada FT/FR/NC com SAF-T AO",
                  "Stock, compras e produção com custos reais",
                  "14 cargos com permissões e auditoria completa",
                  "Segurança com 2FA e backups automáticos",
                ].map((f) => (
                  <p key={f} className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-cyan-400" /> {f}
                  </p>
                ))}
              </div>
              <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.2em] text-zinc-600">
                <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-cyan-500/70" /> Dados encriptados</span>
                <span className="inline-flex items-center gap-2"><Fingerprint className="h-4 w-4 text-cyan-500/70" /> 2FA disponível</span>
              </div>
            </>
          )}
        </div>

        {/* Auth card */}
        <div className="mx-auto w-full max-w-md">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-[0_24px_80px_rgba(0,0,0,0.5)] backdrop-blur-xl">
            <div className="mb-6 flex items-center gap-3 lg:hidden">
              {brandLogo ? <img src={brandLogo} alt={brandName} className="h-10 w-10 rounded-lg object-contain bg-white/5" /> : <img src="/lumora-logo.png" alt="Lumora" className="h-10 w-10" />}
              <span className="lumora-heading text-xl font-extrabold">{brandName}</span>
            </div>

            {/* Tabs */}
            <div className={`mb-6 ${branded ? "hidden" : "grid"} grid-cols-2 rounded-full border border-white/10 bg-black/30 p-1`}>
              <button
                type="button"
                onClick={() => { setActiveTab("login"); setTwoFA({ show: false, tempToken: "", code: "" }); }}
                className={`rounded-full py-2 text-sm font-semibold transition-colors duration-200 ${activeTab === "login" ? "bg-cyan-400 text-black" : "text-zinc-400 hover:text-white"}`}
                style={activeTab === "login" ? accentStyle : undefined}
                data-testid="tab-login"
              >
                Entrar
              </button>
              <button
                type="button"
                onClick={() => { setActiveTab("register"); setStep(1); }}
                className={`rounded-full py-2 text-sm font-semibold transition-colors duration-200 ${activeTab === "register" ? "bg-cyan-400 text-black" : "text-zinc-400 hover:text-white"}`}
                data-testid="tab-register"
              >
                Criar Conta
              </button>
            </div>

            {/* 2FA Step */}
            {activeTab === "login" && twoFA.show && (
              <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="twofa-login-form">
                <div className="rounded-xl border border-cyan-400/30 bg-cyan-400/10 p-3 text-sm text-cyan-200">
                  Verificação em 2 passos ativa. Introduza o código de 6 dígitos da sua app autenticadora.
                </div>
                <div className="space-y-2">
                  <Label className={labelCls} htmlFor="twofa-code">Código 2FA</Label>
                  <Input
                    id="twofa-code"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className={`${inputCls} h-12 text-center text-2xl font-mono tracking-widest`}
                    value={twoFA.code}
                    onChange={(e) => setTwoFA({ ...twoFA, code: e.target.value })}
                    required
                    autoFocus
                    data-testid="login-twofa-code-input"
                  />
                </div>
                <Button type="submit" className="h-12 w-full rounded-full bg-cyan-400 text-base font-bold text-black hover:bg-cyan-300" disabled={loading} data-testid="login-twofa-submit-btn">
                  {loading ? "A verificar..." : "Verificar e Entrar"}
                </Button>
                <Button type="button" variant="ghost" className="w-full text-zinc-400 hover:text-white" onClick={() => setTwoFA({ show: false, tempToken: "", code: "" })}>
                  Voltar
                </Button>
              </form>
            )}

            {/* Login */}
            {activeTab === "login" && !twoFA.show && (
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div className="space-y-2">
                  <Label className={labelCls} htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="o.seu@email.com"
                      className={`${inputCls} pl-10`}
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className={labelCls} htmlFor="login-password">Palavra-passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      className={`${inputCls} pl-10`}
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="h-12 w-full rounded-full bg-cyan-400 text-base font-bold text-black hover:bg-cyan-300" style={accentStyle} disabled={loading} data-testid="login-submit-btn">
                  {loading ? "A entrar..." : "Entrar"}
                  {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </form>
            )}

            {/* Registo - Passo 1 */}
            {activeTab === "register" && step === 1 && !branded && (
              <form onSubmit={handleRegisterStep1} className="space-y-4" data-testid="register-step1-form">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400 text-[10px] font-bold text-black">1</span>
                  Dados Pessoais
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="flex h-5 w-5 items-center justify-center rounded-full border border-white/20 text-[10px] font-bold text-zinc-500">2</span>
                </div>
                <div className="space-y-2">
                  <Label className={labelCls} htmlFor="reg-name">Nome Completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <Input id="reg-name" placeholder="O seu nome" className={`${inputCls} pl-10`} value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })} required data-testid="register-name-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className={labelCls} htmlFor="reg-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <Input id="reg-email" type="email" placeholder="o.seu@email.com" className={`${inputCls} pl-10`} value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} required data-testid="register-email-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className={labelCls} htmlFor="reg-password">Palavra-passe</Label>
                    <Input id="reg-password" type="password" placeholder="••••••••" className={inputCls} value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} required data-testid="register-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className={labelCls} htmlFor="reg-confirm">Confirmar</Label>
                    <Input id="reg-confirm" type="password" placeholder="••••••••" className={inputCls} value={registerForm.confirmPassword} onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })} required data-testid="register-confirm-input" />
                  </div>
                </div>
                <Button type="submit" className="h-12 w-full rounded-full bg-cyan-400 text-base font-bold text-black hover:bg-cyan-300" data-testid="register-next-btn">
                  Continuar <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            )}

            {/* Registo - Passo 2 (empresa) */}
            {activeTab === "register" && step === 2 && (
              <form onSubmit={handleRegisterStep2} className="space-y-4" data-testid="register-step2-form">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full border border-cyan-400/50 text-[10px] font-bold text-cyan-400">✓</span>
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400 text-[10px] font-bold text-black">2</span>
                  Dados da Empresa
                </div>
                <div className="space-y-2">
                  <Label className={labelCls} htmlFor="company-name">Nome da Empresa *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <Input id="company-name" placeholder="Ex: Restaurante Sabores de Luanda" className={`${inputCls} pl-10`} value={registerForm.company_name} onChange={(e) => setRegisterForm({ ...registerForm, company_name: e.target.value })} required data-testid="register-company-name-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className={labelCls} htmlFor="company-nif">NIF *</Label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                      <Input id="company-nif" placeholder="5000000000" className={`${inputCls} pl-10`} value={registerForm.company_nif} onChange={(e) => setRegisterForm({ ...registerForm, company_nif: e.target.value })} required data-testid="register-company-nif-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className={labelCls} htmlFor="company-phone">Telefone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                      <Input id="company-phone" placeholder="+244 9XX XXX XXX" className={`${inputCls} pl-10`} value={registerForm.company_phone} onChange={(e) => setRegisterForm({ ...registerForm, company_phone: e.target.value })} data-testid="register-company-phone-input" />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className={labelCls} htmlFor="company-address">Morada</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <Input id="company-address" placeholder="Rua, Bairro, Luanda" className={`${inputCls} pl-10`} value={registerForm.company_address} onChange={(e) => setRegisterForm({ ...registerForm, company_address: e.target.value })} data-testid="register-company-address-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className={labelCls} htmlFor="company-email">Email da Empresa</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <Input id="company-email" type="email" placeholder="geral@empresa.ao" className={`${inputCls} pl-10`} value={registerForm.company_email} onChange={(e) => setRegisterForm({ ...registerForm, company_email: e.target.value })} data-testid="register-company-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>Logotipo da Empresa (opcional)</Label>
                  {logoPreview ? (
                    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/30 p-3">
                      <img src={logoPreview} alt="Logo" className="h-12 w-12 rounded-lg object-contain" />
                      <span className="flex-1 text-sm text-zinc-400">Logotipo carregado</span>
                      <Button type="button" size="icon" variant="ghost" onClick={removeLogo} data-testid="remove-logo-btn"><X className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-black/20 py-4 text-sm text-zinc-400 transition-colors duration-200 hover:border-cyan-400/40 hover:text-white"
                      data-testid="upload-logo-btn"
                    >
                      <Upload className="h-4 w-4" /> Carregar logotipo (máx. 2MB)
                    </button>
                  )}
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-file-input" />
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>Plano</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPlanChoice("bronze")}
                      className={`rounded-xl border p-3 text-left transition-colors duration-200 ${planChoice === "bronze" ? "border-cyan-400 bg-cyan-400/10" : "border-white/10 bg-black/20 hover:border-white/30"}`}
                      data-testid="plan-option-bronze"
                    >
                      <p className="font-bold text-sm">Bronze</p>
                      <p className="text-xs text-zinc-400">Apenas POS</p>
                      <p className="mt-1 font-mono text-xs text-cyan-300">{cycleChoice === "annual" ? "7.199,20" : "8.999,00"} Kz/mês</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPlanChoice("ouro")}
                      className={`rounded-xl border p-3 text-left transition-colors duration-200 ${planChoice === "ouro" ? "border-cyan-400 bg-cyan-400/10" : "border-white/10 bg-black/20 hover:border-white/30"}`}
                      data-testid="plan-option-ouro"
                    >
                      <p className="font-bold text-sm">Ouro</p>
                      <p className="text-xs text-zinc-400">ERP completo</p>
                      <p className="mt-1 font-mono text-xs text-cyan-300">{cycleChoice === "annual" ? "63.999,99" : "79.999,99"} Kz/mês</p>
                    </button>
                  </div>
                  <div className="grid grid-cols-2 rounded-full border border-white/10 bg-black/30 p-1">
                    <button type="button" onClick={() => setCycleChoice("monthly")} className={`rounded-full py-1.5 text-xs font-semibold transition-colors ${cycleChoice === "monthly" ? "bg-white/15 text-white" : "text-zinc-500"}`} data-testid="cycle-option-monthly">Mensal</button>
                    <button type="button" onClick={() => setCycleChoice("annual")} className={`rounded-full py-1.5 text-xs font-semibold transition-colors ${cycleChoice === "annual" ? "bg-white/15 text-white" : "text-zinc-500"}`} data-testid="cycle-option-annual">Anual −20%</button>
                  </div>
                  <p className="text-xs text-zinc-500">Inclui 14 dias de teste grátis, sem cartão de crédito.</p>
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" className="h-12 flex-1 rounded-full border-white/15 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white" onClick={() => setStep(1)} data-testid="register-back-btn">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
                  </Button>
                  <Button type="submit" className="h-12 flex-1 rounded-full bg-cyan-400 text-base font-bold text-black hover:bg-cyan-300" disabled={loading} data-testid="register-submit-btn">
                    {loading ? "A criar..." : "Criar Empresa"}
                  </Button>
                </div>
              </form>
            )}

            {/* Google */}
            {!twoFA.show && (
              <>
                <div className="my-6 flex items-center gap-3">
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="text-xs uppercase tracking-[0.2em] text-zinc-600">ou</span>
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 w-full rounded-full border-white/15 bg-transparent text-zinc-200 hover:bg-white/5 hover:text-white"
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                >
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continuar com Google
                </Button>
              </>
            )}
          </div>
          <p className="mt-6 text-center text-xs text-zinc-600">
            {branded ? <>Plataforma fornecida por LUMORA · Ao continuar, aceita os Termos de Utilização e a Política de Privacidade.</> : <>Ao continuar, aceita os Termos de Utilização e a Política de Privacidade da Lumora.</>}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
